import { capitalize } from '@mui/material';
import { TranslationKey } from '../types/i18n';
import { ChatDashboardData, Conversation } from '../interfaces/Analytics';

export const getTotalConversations = (data: any): number => {
  if (!data || !data.message || !Array.isArray(data.message)) return 0;
  return data.message.reduce((total: number, entry: any) => {
    const key = Object.keys(entry)[0];
    const chatEntry = entry[key];
    return total + (chatEntry?.total_conversations || 0);
  }, 0);
};

export const getTotalMessages = (data: any): number => {
  if (!data || !data.message || !Array.isArray(data.message)) return 0;
  return data.message.reduce((total: number, entry: any) => {
    const key = Object.keys(entry)[0];
    const chatEntry = entry[key];
    return total + (chatEntry?.total_messages || 0);
  }, 0);
};

export const getAverageSatisfaction = (data: any): number => {
  if (!data || !data.message || !Array.isArray(data.message)) return 0;

  let totalSatisfaction = 0;
  let count = 0;

  data.message.forEach((entry: any) => {
    const key = Object.keys(entry)[0];
    const chatEntry = entry[key];
    if (chatEntry?.average_user_satisfaction !== undefined) {
      totalSatisfaction += chatEntry.average_user_satisfaction;
      count++;
    }
  });

  if (count === 0) return 0;
  return totalSatisfaction / count;
};

export const getAverageSeconds = (data: any): number => {
  if (!data || !data.message || !Array.isArray(data.message)) return 0;

  let totalDuration = 0;
  let totalConversations = 0;

  data.message.forEach((entry: any) => {
    const key = Object.keys(entry)[0];
    const chatEntry = entry[key];
    if (chatEntry?.total_conversations_duration !== undefined) {
      totalDuration += chatEntry.total_conversations_duration || 0;
    }
    if (chatEntry?.total_conversations !== undefined) {
      totalConversations += chatEntry.total_conversations || 0;
    }
  });

  if (totalConversations === 0) return 0;
  return totalDuration / totalConversations;
};

export const getAverageMessages = (data: any): number => {
  if (!data || !data.message || !Array.isArray(data.message)) return 0;

  let totalMessages = 0;
  let totalConversations = 0;

  data.message.forEach((entry: any) => {
    const key = Object.keys(entry)[0];
    const chatEntry = entry[key];
    if (chatEntry?.total_messages !== undefined) {
      totalMessages += chatEntry.total_messages || 0;
    }
    if (chatEntry?.total_conversations !== undefined) {
      totalConversations += chatEntry.total_conversations || 0;
    }
  });

  if (totalConversations === 0) return 0;
  return totalMessages / totalConversations;
};

export const getConversationsPerMonth = (data: any): Record<string, number> => {
  if (!data || !data.message || !Array.isArray(data.message)) return {};

  const conversationsPerMonth: Record<string, number> = {};

  data.message.forEach((entry: any) => {
    const key = Object.keys(entry)[0];
    const chatEntry = entry[key];

    // Use monthly_conversations_number from API response
    if (chatEntry?.monthly_conversations_number) {
      Object.entries(chatEntry.monthly_conversations_number).forEach(
        ([monthYear, count]) => {
          // Convert MM-YYYY format to YYYY-MM format for consistency
          const [month, year] = monthYear.split('-');
          const formattedKey = `${year}-${month}`;
          conversationsPerMonth[formattedKey] =
            (conversationsPerMonth[formattedKey] || 0) + (count as number);
        }
      );
    }
  });

  return conversationsPerMonth;
};

// Same names as libs/languages.py in the backend, each with its label key. Anything else,
// like "anglais" in older chats, shows as it is.
export const LANGUAGE_KEYS: Record<string, TranslationKey> = {
  afrikaans: 'Afrikaans',
  albanian: 'Albanian',
  arabic: 'Arabic',
  armenian: 'Armenian',
  azerbaijani: 'Azerbaijani',
  basque: 'Basque',
  bengali: 'Bengali',
  bosnian: 'Bosnian',
  bulgarian: 'Bulgarian',
  catalan: 'Catalan',
  chinese: 'Chinese',
  croatian: 'Croatian',
  czech: 'Czech',
  danish: 'Danish',
  dutch: 'Dutch',
  english: 'English',
  estonian: 'Estonian',
  filipino: 'Filipino',
  finnish: 'Finnish',
  french: 'French',
  galician: 'Galician',
  georgian: 'Georgian',
  german: 'German',
  greek: 'Greek',
  hebrew: 'Hebrew',
  hindi: 'Hindi',
  hungarian: 'Hungarian',
  icelandic: 'Icelandic',
  indonesian: 'Indonesian',
  irish: 'Irish',
  italian: 'Italian',
  japanese: 'Japanese',
  korean: 'Korean',
  latvian: 'Latvian',
  lithuanian: 'Lithuanian',
  luxembourgish: 'Luxembourgish',
  macedonian: 'Macedonian',
  malay: 'Malay',
  maltese: 'Maltese',
  norwegian: 'Norwegian',
  persian: 'Persian',
  polish: 'Polish',
  portuguese: 'Portuguese',
  romanian: 'Romanian',
  russian: 'Russian',
  serbian: 'Serbian',
  slovak: 'Slovak',
  slovenian: 'Slovenian',
  spanish: 'Spanish',
  swahili: 'Swahili',
  swedish: 'Swedish',
  tamil: 'Tamil',
  thai: 'Thai',
  turkish: 'Turkish',
  ukrainian: 'Ukrainian',
  urdu: 'Urdu',
  vietnamese: 'Vietnamese',
  welsh: 'Welsh',
};

export const languageLabel = (
  language: string | undefined,
  t: (key: TranslationKey) => string
): string => {
  const name = (language || '').trim().toLowerCase();
  if (!name || name === 'n/a') return '';
  return Object.prototype.hasOwnProperty.call(LANGUAGE_KEYS, name)
    ? t(LANGUAGE_KEYS[name])
    : capitalize(name);
};

export const getUniqueChatConversations = (data: any): Conversation[] => {
  const conversations: Conversation[] = [];
  const seen = new Set<string>();

  if (!data || !data.message || !Array.isArray(data.message))
    return conversations;

  data.message.forEach((entry: any) => {
    const agent_id = Object.keys(entry)[0];
    const chatEntry = entry[agent_id];

    if (
      chatEntry?.conversation_details &&
      Array.isArray(chatEntry.conversation_details)
    ) {
      chatEntry.conversation_details.forEach((detail: any) => {
        const conversationId = detail.id || detail.conversation_id;
        if (conversationId) {
          if (seen.has(conversationId)) {
            return;
          }
          seen.add(conversationId);
        }
        // Map API response fields to Conversation interface
        // API returns: id, created_at, ended_at, duration, messages, conversation_summary, etc.
        // Conversation interface expects: conversation_id, start_ts, end_ts, etc.
        const startDate = detail.created_at
          ? new Date(detail.created_at).getTime() / 1000
          : undefined;
        const endDate = detail.ended_at
          ? new Date(detail.ended_at).getTime() / 1000
          : undefined;

        // Build data_collected object from contact fields
        const data_collected: { [key: string]: string | null } = {};
        if (detail.contact_provided_name) {
          data_collected.name = detail.contact_provided_name;
        }
        if (detail.contact_provided_company) {
          data_collected.company = detail.contact_provided_company;
        }
        if (detail.contact_provided_phone_number) {
          data_collected.phone_number = detail.contact_provided_phone_number;
        }
        if (detail.contact_provided_email) {
          data_collected.email = detail.contact_provided_email;
        }

        conversations.push({
          agent_id,
          agent_name: '--', // Will be filled by fillOutAgentNames
          conversation_id: detail.id || detail.conversation_id,
          start_ts: startDate,
          end_ts: endDate,
          status:
            detail.user_satisfaction !== null &&
            detail.user_satisfaction !== undefined
              ? detail.user_satisfaction >= 3
                ? 'RESOLVED'
                : 'UNRESOLVED'
              : 'UNRESOLVED',
          messages: detail.messages || [],
          conversation_summary: detail.conversation_summary || '',
          data_collected,
          // Include other fields that might be needed
          call_cost: 0, // Not applicable for chat
          caller_phone_number: detail.contact_provided_phone_number || '',
          conversation_duration: detail.duration || 0,
          custom_phone_number: '',
          system_phone_number: '',
          category: detail.category,
          conversation_language: detail.conversation_language,
          user_satisfaction: detail.user_satisfaction,
        } as Conversation);
      });
    }
  });

  return conversations;
};

/**
 * Filter locally the chat dashboard data using an agent's ID.
 */
export const filteredChatDashboardData = (
  data: ChatDashboardData | null,
  agentId: string
): ChatDashboardData | null => {
  if (!data || !data.message) return null;

  return {
    ...data,
    message: data.message.filter((entry: any) => {
      const key = Object.keys(entry)[0];
      return key === agentId;
    }),
  };
};
